import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AccountClassForm from './AccountClassForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, Pagination } from '../../../shared/pagination';
import '../../../shared/ListRowHover.css';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import '../../../shared/StyledInput.css';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { askConfirm } from '../../../shared/AppDialogs';

function AccountClassTable() {
  const { canCreate, canUpdate } = useScreenAccess();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [accountCategories, setAccountCategories] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 });
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('sort_order');
  const [sortOrder, setSortOrder] = useState('ASC');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(FALLBACK_PAGE_SIZE);
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(false);

  useDefaultPageSize(setLimit);

  useEffect(() => {
    fetchRows();
    fetchAccountCategories();
    // eslint-disable-next-line
  }, [search, sortBy, sortOrder, page, limit, status]);

  const fetchAccountCategories = async () => {
    try {
      const res = await axios.get('/api/account-categories', { params: { limit: 100 } });
      setAccountCategories(res.data.data || []);
    } catch {
      setAccountCategories([]);
    }
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = { search, sortBy, sortOrder, page, limit, status };
      const res = await axios.get('/api/account-classes', { params });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(col);
      setSortOrder('ASC');
    }
  };

  const handleAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const handleDeactivate = async (item) => {
    if (!(await askConfirm(`Deactivate account class "${item.class_name}"?`, { confirmLabel: 'Deactivate' }))) return;
    try {
      await axios.patch(`/api/account-classes/${item.id}/deactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Deactivate failed');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await axios.patch(`/api/account-classes/${item.id}/reactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Reactivate failed');
    }
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      if (editItem) {
        await axios.put(`/api/account-classes/${editItem.id}`, data);
      } else {
        await axios.post('/api/account-classes', data);
      }
      setShowForm(false);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  const getCategoryLabel = (row) => {
    if (!row.account_category) return '';
    return row.account_category.category_name;
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Account Classes</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canCreate && (
            <button
              onClick={handleAdd}
              disabled={showForm}
              title={showForm ? 'Finish editing first' : undefined}
              style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
            >
              Add Account Class
            </button>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          placeholder="Search code or name..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: 220 }}
        />
        <select className="styled-input" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
          <option value="active">Active items</option>
          <option value="inactive">Deactivated items</option>
        </select>
      </div>

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <AccountClassForm
            key={editItem?.id ?? 'add'}
            initial={editItem}
            accountCategories={accountCategories}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
          />
        </div>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '14%' }} />
            <col style={{ width: '36%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '6%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, cursor: 'pointer', borderBottom: '1px solid #b3bbc7', textAlign: 'left' }} onClick={() => handleSort('class_code')}>Code {sortBy === 'class_code' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, cursor: 'pointer', borderBottom: '1px solid #b3bbc7', textAlign: 'left' }} onClick={() => handleSort('class_name')}>Name {sortBy === 'class_name' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, cursor: 'pointer', borderBottom: '1px solid #b3bbc7', textAlign: 'left' }} onClick={() => handleSort('account_category_id')}>Account Category {sortBy === 'account_category_id' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, cursor: 'pointer', borderBottom: '1px solid #b3bbc7', textAlign: 'right' }} onClick={() => handleSort('sort_order')}>Sort {sortBy === 'sort_order' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Flags</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => (
              <tr
                key={item.id}
                className="list-row"
                onClick={() => { if (!showForm && canUpdate) handleEdit(item); }}
                style={{ cursor: showForm || !canUpdate ? 'default' : 'pointer' }}
                title={showForm || !canUpdate ? undefined : 'Click to edit'}
              >
                <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>{item.class_code}</td>
                <td style={{ padding: '6px 8px', fontSize: 14 }}>{item.class_name}</td>
                <td style={{ padding: '6px 8px', fontSize: 14 }}>{getCategoryLabel(item)}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 14 }}>{item.sort_order}</td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 12 }}>
                  {item.is_active ? 'A' : '-'} {item.system_locked ? 'L' : '-'}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu
                    items={[
                      { key: 'edit', label: 'Edit', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleEdit(item) },
                      status === 'active'
                        ? {
                            key: 'deactivate',
                            label: 'Deactivate',
                            danger: true,
                            disabled: showForm || item.system_locked,
                            disabledTitle: showForm ? 'Finish editing first' : 'System-locked records cannot be deactivated',
                            onClick: () => handleDeactivate(item),
                          }
                        : {
                            key: 'reactivate',
                            label: 'Reactivate',
                            disabled: showForm,
                            disabledTitle: showForm ? 'Finish editing first' : undefined,
                            onClick: () => handleReactivate(item),
                          },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Account Classes"
        rightContent={(
          <select className="styled-input"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}
          >
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default AccountClassTable;
